import type { LucideIcon } from 'lucide-react';
import { CircleAlert, CircleCheck, Clock, Info, TriangleAlert, X } from 'lucide-react';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
    isNoticeVisible,
    NOTICE_LEVEL_LABELS,
    type Notice,
    type NoticeLevel,
    type NoticePlacement,
} from '@/domain/notices';
import { useActiveNotice } from '@/hooks/queries/useNoticesQuery';
import { usePersistedSet } from '@/hooks/usePersistedState';
import { formatRelativeDateTime } from '@/lib/formatters';
import { cn } from '@/lib/utils';

/** Must match `--duration-base`; the row collapses before it unmounts. */
const DISMISS_MS = 150;

interface StripStyle {
    /** Severity has to read from the shape, never from colour alone. */
    icon: LucideIcon;
    iconColor: string;
    surface: string;
    /** Kept apart from `surface`: the area pill borrows the border over white. */
    border: string;
    title: string;
    text: string;
}

/**
 * Outage is always a bar, so it needs no strip tint.
 *
 * The design draws each icon as a bare glyph (a lone "!", "i", "✓") inside a
 * filled circle. Lucide ships those glyphs only pre-enclosed, and a ringed icon
 * inside another filled circle reads as a badge-in-a-badge — so the icon is the
 * enclosure here, tinted with the level colour.
 */
const STRIP_STYLES: Record<Exclude<NoticeLevel, 'outage'>, StripStyle> = {
    warning: {
        icon: TriangleAlert,
        iconColor: 'text-warning',
        surface: 'bg-[var(--notice-warning-surface)]',
        border: 'border-[var(--notice-warning-border)]',
        title: 'text-[var(--notice-warning-title)]',
        text: 'text-[var(--notice-warning-text)]',
    },
    maintenance: {
        icon: Clock,
        iconColor: 'text-muted-foreground',
        surface: 'bg-muted',
        border: 'border-border',
        title: 'text-foreground',
        text: 'text-foreground-secondary',
    },
    info: {
        icon: Info,
        iconColor: 'text-info',
        surface: 'bg-[var(--notice-info-surface)]',
        border: 'border-[var(--notice-info-border)]',
        title: 'text-[var(--notice-info-title)]',
        text: 'text-[var(--notice-info-text)]',
    },
    resolved: {
        icon: CircleCheck,
        iconColor: 'text-success',
        surface: 'bg-[var(--notice-resolved-surface)]',
        border: 'border-[var(--notice-resolved-border)]',
        title: 'text-[var(--notice-resolved-title)]',
        text: 'text-[var(--notice-resolved-text)]',
    },
};

/**
 * A bare "14:02" reads as today, which is wrong for a notice that has been up
 * for days — so this ages into "Yesterday at 14:02", then "3 days ago".
 */
function noticeTime(notice: Notice): string {
    return formatRelativeDateTime(notice.displayed_at ?? notice.created_at);
}

interface NoticeViewProps {
    notice: Notice;
    onDismiss?: () => void;
}

/** The solid bar an outage gets: the app visibly sits below an incident. */
export function NoticeBar({ notice, onDismiss }: NoticeViewProps) {
    return (
        <div
            role="alert"
            className="flex items-center gap-3.5 bg-[var(--notice-outage-bar)] px-5 py-2.5 text-white"
        >
            <div className="flex shrink-0 items-center gap-2">
                <CircleAlert className="h-4 w-4" />
                <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-white/85">
                    {NOTICE_LEVEL_LABELS.outage}
                </span>
            </div>
            <span className="h-4 w-px bg-white/25" />
            <div className="flex flex-1 flex-wrap items-baseline gap-x-2 gap-y-1">
                <span className="text-sm font-semibold">{notice.headline}</span>
                {notice.detail && <span className="text-sm text-white/80">{notice.detail}</span>}
            </div>
            {notice.area && (
                <span className="inline-flex shrink-0 items-center rounded-md bg-white/15 px-2 py-0.5 text-xs font-medium">
                    {notice.area}
                </span>
            )}
            <span className="shrink-0 text-xs text-white/75">{noticeTime(notice)}</span>
            {notice.link && (
                <a
                    href={notice.link}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`Notice details: ${notice.headline}`}
                    className="inline-flex shrink-0 items-center rounded-lg bg-white px-2.5 py-1 text-[13px] font-medium text-[var(--notice-outage-bar)]"
                >
                    Notice details
                </a>
            )}
            {onDismiss && (
                <button
                    type="button"
                    aria-label="Dismiss notice"
                    onClick={onDismiss}
                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-white/80 transition-colors hover:bg-white/20 hover:text-white"
                >
                    <X className="h-3.5 w-3.5" />
                </button>
            )}
        </div>
    );
}

/** The in-content strip every non-outage level gets. */
export function NoticeStrip({ notice, onDismiss }: NoticeViewProps) {
    const level = notice.level === 'outage' ? 'warning' : notice.level;
    const style = STRIP_STYLES[level];
    const Icon = style.icon;

    return (
        <output
            aria-live="polite"
            aria-label={`${NOTICE_LEVEL_LABELS[notice.level]}: ${notice.headline}`}
            className={cn(
                'flex items-start gap-3 rounded-xl border px-3.5 py-3',
                style.surface,
                style.border
            )}
        >
            <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', style.iconColor)} />
            <div className="flex flex-1 flex-wrap items-baseline gap-x-2 gap-y-1">
                <span className={cn('text-sm font-semibold', style.title)}>{notice.headline}</span>
                {notice.detail && (
                    <span className={cn('text-sm', style.text)}>{notice.detail}</span>
                )}
            </div>
            <div className="flex shrink-0 items-center gap-2.5">
                {notice.area && (
                    <span
                        className={cn(
                            'inline-flex items-center rounded-md border bg-card px-2 py-0.5 text-xs font-medium',
                            style.border,
                            style.text
                        )}
                    >
                        {notice.area}
                    </span>
                )}
                <span className={cn('font-mono text-xs', style.text)}>{noticeTime(notice)}</span>
                {notice.link && (
                    <a
                        href={notice.link}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={`Notice details: ${notice.headline}`}
                        className={cn(
                            'text-[13px] font-medium underline underline-offset-2',
                            style.text
                        )}
                    >
                        Notice details
                    </a>
                )}
                {onDismiss && (
                    <button
                        type="button"
                        aria-label="Dismiss notice"
                        onClick={onDismiss}
                        className={cn(
                            'flex h-6 w-6 items-center justify-center rounded-md transition-opacity hover:opacity-70',
                            style.text
                        )}
                    >
                        <X className="h-3.5 w-3.5" />
                    </button>
                )}
            </div>
        </output>
    );
}

interface NoticeBannerProps {
    placement: NoticePlacement;
    /** Gutter the strip inherits from the page it sits in. */
    className?: string;
}

/**
 * Staff-authored notice (IX-4223). Severity decides where it renders and how
 * far it reaches — see `isNoticeVisible`, the only place that rule lives.
 * One notice is displayed at a time; dismissal is stored per notice id, so
 * displaying a different notice re-opens the banner for everyone.
 */
export function NoticeBanner({ placement, className }: NoticeBannerProps) {
    const { data: notice } = useActiveNotice();
    const { pathname } = useLocation();
    const [dismissed, toggleDismissed] = usePersistedSet('dismissed-notices', new Set<string>());
    const [closingNoticeId, setClosingNoticeId] = useState<string | null>(null);

    if (!notice || dismissed.has(notice.id)) return null;
    if (!isNoticeVisible(notice, placement, pathname)) return null;

    // Collapse the row before committing the dismissal, so the page below
    // doesn't jump. Nothing (not even a spacer) renders once it's gone.
    const dismiss = () => {
        setClosingNoticeId(notice.id);
        window.setTimeout(() => toggleDismissed(notice.id), DISMISS_MS);
    };

    return (
        <div
            className={cn(
                'grid transition-[grid-template-rows,opacity] duration-[var(--duration-base)] ease-[var(--easing)]',
                closingNoticeId === notice.id ? 'grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr]',
                className
            )}
        >
            <div className="overflow-hidden">
                {placement === 'bar' ? (
                    <NoticeBar notice={notice} onDismiss={dismiss} />
                ) : (
                    <NoticeStrip notice={notice} onDismiss={dismiss} />
                )}
            </div>
        </div>
    );
}
